import { useCallback, useEffect, useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Stack } from "@similarweb/ui-components/foundations";

import { QueryStates } from "app/pages/sales-intelligence/common-components/QueryStates/QueryStates";
import { PLAYBOOK_EDIT_ROUTE, PLAYBOOKS_ROUTE } from "app/pages/sales-intelligence/constants/sl_routes";
import { VisitsFromSingleCountryProvider } from "app/pages/sales-intelligence/modules/leads-search-filters/context/VisitsFromSingleCountryContext";
import { PlaybookWizard } from "app/pages/sales-intelligence/modules/playbooks/components/PlaybookWizard/PlaybookWizard";
import { usePlaybookQuery } from "app/pages/sales-intelligence/modules/playbooks/queries/usePlaybookQuery";
import { StyledManagementSIBreadcrumbs } from "app/pages/sales-intelligence/pages/management/containers/SavedSearchWebhooksManagementContainer/styles";
import { useScoringFiltersBootstrap } from "app/pages/sales-intelligence/modules/lead-scoring-agent/hooks/useScoringFiltersBootstrap";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";
import { proNavigator } from "single-spa/react-app/ProNavigator";

export const PlaybookWizardContainer = () => {
    const { translate } = useTranslate();
    const { params, state } = useCurrentStateAndParams();

    const isEditMode = state.name === PLAYBOOK_EDIT_ROUTE;
    const parsedPlaybookId = Number(params.id);
    const hasValidPlaybookId = Number.isFinite(parsedPlaybookId);
    const playbookId = parsedPlaybookId;
    const initialIsSharedWithAccount = !isEditMode && params.shared === true;

    useEffect(() => {
        if (isEditMode && !hasValidPlaybookId) {
            void proNavigator.go(PLAYBOOKS_ROUTE);
        }
    }, [isEditMode, hasValidPlaybookId]);

    const {
        data: playbook,
        isLoading,
        isError,
        refetch,
    } = usePlaybookQuery({ playbookId, enabled: isEditMode && hasValidPlaybookId });

    const showLoadingState = isEditMode && isLoading;
    const showErrorState = isEditMode && !isLoading && (isError || !playbook);
    const showQueryStates = showLoadingState || showErrorState;

    const breadcrumbs = useMemo(
        () => [
            { state: PLAYBOOKS_ROUTE, title: translate("si.playbooks.header") },
            {
                state: "",
                title: isEditMode
                    ? (playbook?.name ?? translate("si.playbooks.wizard.title.edit"))
                    : translate("si.playbooks.wizard.title.new"),
            },
        ],
        [isEditMode, playbook?.name, translate],
    );

    const handleBreadcrumbClick = useCallback(({ state: breadcrumbState }: { state: string }) => {
        if (breadcrumbState) {
            void proNavigator.go(breadcrumbState);
        }
    }, []);

    const handleRetryQueries = useCallback(() => {
        void refetch();
    }, [refetch]);

    const handleDone = useCallback(() => {
        void siNavigator.goToPlaybooksPage();
    }, []);

    const { filtersList, isBootstrapping } = useScoringFiltersBootstrap();

    return (
        <Stack
            flexGrow={1}
            minHeight="0"
            data-automation="playbook-wizard-container"
        >
            <StyledManagementSIBreadcrumbs
                list={breadcrumbs}
                onClick={handleBreadcrumbClick}
                dataAutomation="playbook-wizard-breadcrumbs"
            />
            <Stack
                flex="1"
                width="100%"
                minHeight="calc(100vh - 94px)"
            >
                {showQueryStates ? (
                    <QueryStates
                        isLoading={showLoadingState}
                        isError={showErrorState}
                        onRetry={handleRetryQueries}
                        errorDataAutomation="playbook-wizard-query-error"
                        errorRetryDataAutomation="playbook-wizard-query-error-retry"
                    />
                ) : (
                    <VisitsFromSingleCountryProvider value>
                        <PlaybookWizard
                            key={isEditMode ? playbookId : `create-${initialIsSharedWithAccount}`}
                            mode={isEditMode ? "edit" : "create"}
                            playbook={playbook}
                            initialIsSharedWithAccount={initialIsSharedWithAccount}
                            onDone={handleDone}
                            filtersList={filtersList}
                            isBootstrapping={isBootstrapping}
                        />
                    </VisitsFromSingleCountryProvider>
                )}
            </Stack>
        </Stack>
    );
};
